/**
 * LeadSearchPage — Search across all leads (debounced).
 *
 * Apr 28, 2026 (Squad-D). Screen 09 of 12.
 *
 * - Centered search input · debounced (300ms) → GET /leads?q=…
 * - Status filter chips (All / Unverified / Verified / Assigned / Rejected)
 * - Recent searches (localStorage)
 * - Results card with status chip + Open button
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Clock } from 'lucide-react';
import { safeGet } from '@/pages/dashboard/roles/_dashboardShared';
import {
  PageShell,
  EmptyState,
  type Lead,
  unwrapList,
  leadName,
  leadPhone,
} from './_lmShared';

type StatusKey = 'all' | 'unverified' | 'verified' | 'assigned' | 'rejected';

const RECENT_KEY = 'aura.lm.recentSearches.v1';

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((s) => typeof s === 'string') : [];
  } catch {
    return [];
  }
}

function pushRecent(q: string) {
  if (!q.trim()) return;
  const prev = readRecent().filter((s) => s !== q);
  const next = [q, ...prev].slice(0, 6);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

function deriveStatusTone(status: string | null | undefined): 'amber' | 'green' | 'blue' | 'red' | '' {
  const s = (status ?? '').toLowerCase();
  if (s.includes('reject')) return 'red';
  if (s.includes('verified')) return 'green';
  if (s.includes('assigned')) return 'blue';
  if (s.includes('pending') || s.includes('unverified')) return 'amber';
  return '';
}

export function LeadSearchPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [results, setResults] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusKey>('all');
  const [recent, setRecent] = useState<string[]>(readRecent());
  const inputRef = useRef<HTMLInputElement>(null);

  /* Debounce. */
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  /* Fetch on debounced query change. */
  useEffect(() => {
    if (!debouncedQ) {
      setResults([]);
      return;
    }
    setLoading(true);
    safeGet<unknown>(`/lead-integrations/lead-imports?search=${encodeURIComponent(debouncedQ)}&limit=50`).then((r) => {
      const raw = (r.data as any)?.imports ?? r.data;
      setResults(unwrapList<Lead>(raw));
      setLoading(false);
      pushRecent(debouncedQ);
      setRecent(readRecent());
    });
  }, [debouncedQ]);

  /* Bucket counts for chips. */
  const counts = useMemo(() => {
    const c: Record<StatusKey, number> = {
      all: results.length,
      unverified: 0,
      verified: 0,
      assigned: 0,
      rejected: 0,
    };
    results.forEach((l) => {
      const v = (l.verificationStatus ?? '').toLowerCase();
      const s = (l.status ?? '').toLowerCase();
      if (v.includes('pending') || v.includes('unverified')) c.unverified += 1;
      if (v.includes('verified') && !s.includes('assigned')) c.verified += 1;
      if (s.includes('assigned')) c.assigned += 1;
      if (s.includes('reject') || v.includes('reject')) c.rejected += 1;
    });
    return c;
  }, [results]);

  const filtered = useMemo(() => {
    if (statusFilter === 'all') return results;
    return results.filter((l) => {
      const v = (l.verificationStatus ?? '').toLowerCase();
      const s = (l.status ?? '').toLowerCase();
      switch (statusFilter) {
        case 'unverified':
          return v.includes('pending') || v.includes('unverified');
        case 'verified':
          return v.includes('verified') && !s.includes('assigned');
        case 'assigned':
          return s.includes('assigned');
        case 'rejected':
          return s.includes('reject') || v.includes('reject');
      }
    });
  }, [results, statusFilter]);

  /* "/" focuses the search bar. */
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === '/') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <PageShell title="Search" sub="Across all leads · verified, unverified, assigned, rejected">
      <div style={{ maxWidth: 720, margin: '0 auto' }}>
        {/* Search bar */}
        <div
          style={{
            background: 'var(--ivory-1)',
            border: '1px solid var(--line)',
            borderRadius: 12,
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            boxShadow: 'var(--sh-1)',
          }}
        >
          <Search size={18} style={{ color: 'var(--ink-3)' }} />
          <input
            ref={inputRef}
            autoFocus
            placeholder="Search by phone, name, product, source…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              flex: 1,
              border: 'none',
              background: 'transparent',
              fontSize: 16,
              outline: 'none',
              color: 'var(--ink-0)',
              fontFamily: 'inherit',
            }}
          />
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
            {loading ? 'searching…' : results.length > 0 ? `${results.length} results` : ''}
          </span>
        </div>

        {/* Filter chips — only when there are results */}
        {results.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 14, flexWrap: 'wrap' }}>
            {(['all', 'unverified', 'verified', 'assigned', 'rejected'] as StatusKey[]).map((k) => (
              <button
                key={k}
                onClick={() => setStatusFilter(k)}
                className="chip"
                style={{
                  cursor: 'pointer',
                  background: statusFilter === k ? 'var(--ink-0)' : undefined,
                  color: statusFilter === k ? 'var(--ivory-0)' : undefined,
                  border:
                    '1px solid ' + (statusFilter === k ? 'var(--ink-0)' : 'var(--line)'),
                  textTransform: 'capitalize',
                }}
              >
                {k} · {counts[k]}
              </button>
            ))}
          </div>
        )}

        {/* Recent searches */}
        {!debouncedQ && recent.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--ink-3)',
                textTransform: 'uppercase',
                letterSpacing: 0.06,
                fontWeight: 500,
                marginBottom: 8,
              }}
            >
              Recent searches
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {recent.map((r) => (
                <button key={r} className="btn btn-sm btn-outline" onClick={() => setQ(r)}>
                  <Clock size={11} /> {r}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Results */}
        <div style={{ marginTop: 24 }}>
          {!debouncedQ ? null : loading ? (
            <EmptyState title="Searching…" />
          ) : filtered.length === 0 ? (
            <EmptyState
              title={`No leads matching "${debouncedQ}"`}
              sub="Try a phone number, full or partial customer name, or a source name."
            />
          ) : (
            <>
              <div
                style={{
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: 0.06,
                  fontWeight: 500,
                  marginBottom: 8,
                }}
              >
                Results · "{debouncedQ}"
              </div>
              <div className="card" style={{ overflow: 'hidden' }}>
                {filtered.map((l, i) => {
                  const tone = deriveStatusTone(l.verificationStatus ?? l.status);
                  const tag =
                    l.status === 'assigned'
                      ? `Assigned${l.teamName ? ' · ' + l.teamName : ''}`
                      : l.verificationStatus ?? l.status ?? '—';
                  return (
                    <div
                      key={l.id}
                      style={{
                        padding: '12px 16px',
                        borderBottom: i === filtered.length - 1 ? 'none' : '1px solid var(--ivory-3)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 12,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 500 }}>{leadName(l)}</div>
                        <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                          <span className="mono">{l.id.slice(-6)}</span> · {leadPhone(l)}
                        </div>
                      </div>
                      <span className={`chip ${tone ? 'chip-' + tone : ''}`}>{tag}</span>
                      <button
                        className="btn btn-sm btn-outline"
                        onClick={() => navigate(`/lead-manager/leads/${l.id}`)}
                      >
                        Open
                      </button>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    </PageShell>
  );
}

export default LeadSearchPage;
